import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, ArrowRight, AlertCircle, Users, Loader2, CheckCircle2, Key, LogIn, UserPlus } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Card, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import * as Invite from 'modules/invite';
import * as Organization from 'modules/organization';
import { useAuthContext, isCandidateExperience } from 'modules/auth';
import { useTranslation } from 'react-i18next';
import config from 'config';
type InviteType = 'org' | 'test' | 'team' | null;
const detectCodeType = (code: string): InviteType => {
    if (code.startsWith('ORG-'))
        return 'org';
    if (code.startsWith('TEAM-'))
        return 'team';
    if (code.startsWith('ZT-'))
        return 'test';
    return null;
};
const PublicTestEntry: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { code: urlCode } = useParams<{
        code: string;
    }>();
    const { state: authState, methods: authMethods } = useAuthContext();
    const isAuthenticated = authState.isAuthenticated;
    const isStudent = isCandidateExperience(authState.user?.accountType);
    // Candidates take tests sealed inside their /portal shell; staff use /app.
    const takeBasePath = isStudent
        ? '/portal/tests/take'
        : '/app/tests/take';
    const [code, setCode] = useState(urlCode || '');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    // Org invite state and preview metadata
    const [orgInvite, setOrgInvite] = useState(false);
    const [orgInfo, setOrgInfo] = useState<{
        code: string;
        role: string;
        organization: {
            id: string;
            name: string;
            slug?: string;
        };
    } | null>(null);
    const [joinSuccess, setJoinSuccess] = useState(false);
    // Real org name from the join response, shown on the success screen.
    const [joinedOrgName, setJoinedOrgName] = useState<string | null>(null);
    // Test invite state
    const [testInfo, setTestInfo] = useState<{
        testId: string;
        title: string;
        description?: string;
    } | null>(null);
    // Auto-validate if code is in URL
    useEffect(() => {
        if (urlCode) {
            validateCode(urlCode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlCode]);
    const validateCode = async (codeToValidate: string) => {
        const trimmed = codeToValidate.trim();
        if (!trimmed)
            return;
        setError('');
        setIsLoading(true);
        setOrgInvite(false);
        setOrgInfo(null);
        setTestInfo(null);
        const type = detectCodeType(trimmed);
        try {
            if (type === 'team') {
                // Team join codes route to the dedicated team-join landing,
                // which validates and runs the same join flow as the link.
                navigate(`/join/team/${trimmed}`);
                return;
            }
            if (type === 'org') {
                const { data } = await Organization.Api.ValidateOrgInvite(trimmed) as any;
                sessionStorage.setItem('pending_org_invite_code', trimmed);
                setOrgInfo({
                    code: data.code || trimmed,
                    role: data.role,
                    organization: data.organization,
                });
                setOrgInvite(true);
            }
            else {
                // Test invite — validate publicly
                const { data } = await Invite.Api.Validate(trimmed) as any;
                setTestInfo({
                    testId: data.testId?.toString() || data.test?.id || data.id,
                    title: data.test?.title || t('assessment'),
                    description: data.test?.description,
                });
            }
        }
        catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Invalid or expired invite code');
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = code.trim();
        if (!trimmed)
            return;
        const type = detectCodeType(trimmed);
        // If we already have validated info, proceed with the action
        if (type === 'test' && testInfo) {
            navigate(`${takeBasePath}/${testInfo.testId}?invite=${trimmed}`);
            return;
        }
        // Otherwise validate first
        await validateCode(trimmed);
    };
    const handleJoinOrg = async () => {
        const trimmed = (orgInfo?.code || code).trim();
        setIsLoading(true);
        setError('');
        try {
            const { data } = await Organization.Api.JoinByInvite(trimmed) as any;
            sessionStorage.removeItem('pending_org_invite_code');
            const orgId = data?.organization?.id || data?.organization?._id;
            if (orgId) {
                localStorage.setItem(config.api.active_org_key, orgId);
            }
            setJoinedOrgName(data?.organization?.name ?? orgInfo?.organization.name ?? null);
            setJoinSuccess(true);
        }
        catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Failed to join organization');
        }
        finally {
            setIsLoading(false);
        }
    };
    const goToSignup = () => {
        const inviteCode = orgInfo?.code || code.trim();
        if (inviteCode) {
            sessionStorage.setItem('pending_org_invite_code', inviteCode);
        }
        navigate('/signup', {
            state: { from: { pathname: `/join/${inviteCode}` }, orgInviteCode: inviteCode },
        });
    };
    const goToLogin = () => {
        const inviteCode = orgInfo?.code || code.trim();
        if (inviteCode) {
            sessionStorage.setItem('pending_org_invite_code', inviteCode);
        }
        navigate('/login', {
            state: { from: { pathname: `/join/${inviteCode}` }, orgInviteCode: inviteCode },
        });
    };
    const handleStartTest = () => {
        if (!testInfo)
            return;
        if (!isAuthenticated) {
            navigate(`/login`, { state: { from: { pathname: `/join/${code.trim()}` } } });
            return;
        }
        navigate(`${takeBasePath}/${testInfo.testId}?invite=${code.trim()}`);
    };
    // Success state after joining org
    if (joinSuccess) {
        return (<div className="min-h-screen flex items-center justify-center p-6 bg-muted/20">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md">
          <Card>
            <CardContent className="p-8 text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 size={32} className="text-green-600 dark:text-green-400"/>
              </div>
              <h2 className="text-xl font-bold text-foreground">{t("successfully_joined")}</h2>
              <p className="text-muted-foreground">
                {joinedOrgName
                  ? t('you_have_been_added_to_org', { defaultValue: 'You have been added to {{org}}.', org: joinedOrgName })
                  : t("you_have_been_added_to_the_organization")}
              </p>
              <Button className="w-full" onClick={() => { window.location.href = '/app'; }}>{t("go_to_dashboard")}</Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>);
    }
    // Org invite confirmation UI. Keep showing this card even when `error` is set
    // so a failed join renders the error inline (next to a re-clickable "Join"
    // button) instead of dropping the user back to the bare code-entry form.
    if (orgInvite && orgInfo) {
        return (<div className="min-h-screen flex items-center justify-center p-6 bg-muted/20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <Users size={24} className="text-foreground" aria-hidden/>
            </div>
            <h1 className="mb-2 text-2xl font-bold tracking-[-0.02em] text-foreground">
              {orgInfo.organization.name}
            </h1>
            <p className="text-muted-foreground">
              {orgInfo.role === 'NO_ACCESS'
                ? t('you_ve_been_invited_to_join', {
                    defaultValue: "You've been invited to join {{org}}.",
                    org: orgInfo.organization.name,
                  })
                : t('you_ve_been_invited_to_join_as', {
                    defaultValue: "You've been invited to join {{org}} as {{role}}.",
                    org: orgInfo.organization.name,
                    role: orgInfo.role,
                  })}
            </p>
          </div>

          <Card>
            <CardContent className="p-6 space-y-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t('organization', { defaultValue: 'Organization' })}</span>
                  <span className="font-semibold text-foreground">{orgInfo.organization.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t('role', { defaultValue: 'Role' })}</span>
                  <Badge variant={orgInfo.role === 'NO_ACCESS' ? 'warning' : 'secondary'} className="font-semibold">
                    {orgInfo.role === 'NO_ACCESS' ? t('role_no_access', { defaultValue: 'No Access' }) : orgInfo.role}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t('invite_code')}</span>
                  <code className="font-mono text-sm font-semibold text-foreground">{orgInfo.code}</code>
                </div>
              </div>

              {error && (<div role="alert" className="flex items-center gap-2 rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-900/50 dark:bg-error-900/20 dark:text-error-400">
                  <AlertCircle size={16} className="shrink-0"/>
                  <span>{error}</span>
                </div>)}

              {isAuthenticated && isStudent ? (
                <div className="space-y-3">
                  <div role="alert" className="flex items-start gap-2 rounded-lg border border-amber-300/40 bg-amber-50/60 p-4 text-sm dark:border-amber-500/20 dark:bg-amber-500/10">
                    <AlertCircle size={18} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                    <div>
                      <p className="font-semibold text-foreground">
                        {t('auth.candidate_cannot_join_staff_title', { defaultValue: 'Use a staff account' })}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {t('auth.candidate_cannot_join_staff_desc', {
                          defaultValue: 'Student accounts cannot accept staff invitations. Please log in with a staff account or create a new one.',
                        })}
                      </p>
                    </div>
                  </div>
                  <Button onClick={() => authMethods.logout()} variant="outline" className="w-full" size="lg">
                    <LogIn size={16} className="mr-2" />
                    {t('auth.sign_out_and_switch', { defaultValue: 'Sign out & switch account' })}
                  </Button>
                </div>
              ) : isAuthenticated ? (
                <Button className="w-full" size="lg" onClick={handleJoinOrg} disabled={isLoading}>
                  {isLoading ? (<>
                      <Loader2 size={16} className="mr-2 animate-spin"/>{t("joining")}</>) : (<>
                      <Users size={16} className="mr-2"/>
                      {orgInfo.role === 'NO_ACCESS'
                        ? t('join_organization', { defaultValue: 'Join organization' })
                        : t('join_as_role', {
                            defaultValue: 'Join as {{role}}',
                            role: orgInfo.role,
                          })}
                    </>)}
                </Button>
              ) : (
                <div className="space-y-3">
                  <Button className="w-full" size="lg" onClick={goToLogin}>
                    <LogIn size={16} className="mr-2" />
                    {t('teams.join_page_cta_login', { defaultValue: 'Log in' })}
                  </Button>
                  <Button variant="outline" className="w-full" size="lg" onClick={goToSignup}>
                    <UserPlus size={16} className="mr-2" />
                    {t('teams.join_page_cta_signup', { defaultValue: 'Sign up' })}
                  </Button>
                  <p className="text-center text-xs text-muted-foreground">
                    {t('auth.sign_in_required_to_join', {
                      defaultValue: 'You must be signed in to accept this invitation.',
                    })}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <p className="text-center mt-6 text-sm text-muted-foreground">{t("wrong_invite")}{' '}
            <button type="button" className="font-semibold text-foreground underline underline-offset-[3px] hover:decoration-2" onClick={() => {
                setOrgInvite(false);
                setOrgInfo(null);
                setError('');
                setCode('');
                sessionStorage.removeItem('pending_org_invite_code');
            }}>{t("enter_a_different_code")}</button>
          </p>
        </motion.div>
      </div>);
    }
    // Test invite confirmation UI
    if (testInfo && !error) {
        return (<div className="min-h-screen flex items-center justify-center p-6 bg-muted/20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <ShieldCheck size={24} className="text-foreground" aria-hidden/>
            </div>
            <h1 className="mb-2 text-2xl font-bold tracking-[-0.02em] text-foreground">{t("ready_to_start")}</h1>
            <p className="text-muted-foreground">{t("your_invite_code_has_been_validated")}</p>
          </div>

          <Card>
            <CardContent className="p-6 space-y-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t("assessment")}</span>
                  <span className="font-semibold text-foreground">{testInfo.title}</span>
                </div>
                {testInfo.description && (<p className="text-sm text-muted-foreground">{testInfo.description}</p>)}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t("code")}</span>
                  <code className="font-mono text-sm font-semibold text-foreground">{code}</code>
                </div>
              </div>

              <Button className="w-full" size="lg" onClick={handleStartTest}>{t("start_assessment")}<ArrowRight size={16} className="ml-2"/>
              </Button>

              {!isAuthenticated && (<p className="text-center text-xs text-warning-700 dark:text-warning-400">{t("you_ll_need_to_log_in_or_create_an_account_before_starting")}</p>)}
            </CardContent>
          </Card>

          <p className="text-center mt-6 text-sm text-muted-foreground">{t("wrong_code")}{' '}
            <button type="button" className="font-semibold text-foreground underline underline-offset-[3px] hover:decoration-2" onClick={() => {
                setTestInfo(null);
                setCode('');
            }}>{t("enter_a_different_code")}</button>
          </p>
        </motion.div>
      </div>);
    }
    // Default: code entry form
    return (<div className="min-h-screen flex items-center justify-center p-6 bg-muted/20">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <Key size={24} className="text-foreground" aria-hidden/>
          </div>
          <h1 className="mb-2 text-2xl font-bold tracking-[-0.02em] text-foreground">{t("enter_invite_code")}</h1>
          <p className="text-muted-foreground">{t("enter_your_code_to_join_an_organization_or_start_an_assessment")}</p>
        </div>

        <Card>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (<div role="alert" className="flex items-center gap-2 rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-900/50 dark:bg-error-900/20 dark:text-error-400">
                  <AlertCircle size={16}/>
                  {error}
                </div>)}

              <Input placeholder={t("org_xxxxxxxx_or_zt_xxx_xxx_xxx")} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} className="text-center text-lg font-mono tracking-wider"/>

              <Button type="submit" className="w-full" size="lg" disabled={isLoading || !code.trim()}>
                {isLoading ? (<>
                    <Loader2 size={16} className="mr-2 animate-spin"/>{t("validating")}</>) : (<>{t("continue")}<ArrowRight size={16} className="ml-2"/>
                  </>)}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center mt-6 text-sm text-muted-foreground">{t("don_t_have_an_invite_code")}{' '}
          <button type="button" className="font-semibold text-foreground underline underline-offset-[3px] hover:decoration-2" onClick={() => navigate('/login')}>{t("log_in_instead")}</button>
        </p>
      </motion.div>
    </div>);
};
export default PublicTestEntry;
